import React, { useState } from 'react';
import styled from 'styled-components';
import Input from '../UI/Input';
import Btn from '../UI/Btn';
import { colors } from '@/constants/colors';
import { createStore } from '@/api/store';
import { toast } from 'react-toastify';
import { useUIStore } from '@/store/useUIStore';

// interface Props {
//    // sample: string;
//    handleClose: () => void;
// }

type ValuesState = {
   storename: string;
   location: string;
};

type ErrorState = {
   storename: boolean;
   location: boolean;
};

const CreateStore = () => {
   const { handleClose, handleRefresh } = useUIStore();
   const [values, setValues] = useState<ValuesState>({
      storename: '',
      location: '',
   });
   const [isLoading, setIsLoading] = useState(false);
   const [errors, setErrors] = useState<ErrorState>({
      storename: false,
      location: false,
   });

   const { storename, location } = values;

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setValues({ ...values, [name]: value });
      // Clear error when user starts typing
      setErrors((prev) => ({ ...prev, [name]: false }));
   };

   const validateForm = () => {
      const newErrors = {
         storename: !storename.trim(),
         location: !location.trim(),
      };
      setErrors(newErrors);
      return !Object.values(newErrors).some((error) => error);
   };

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (isLoading) {
         return;
      }

      if (!validateForm()) {
         toast.error('Please fill in all fields');
         return;
      }

      try {
         setIsLoading(true);
         const response = await createStore(values);

         if (response.status === 200) {
            toast.success(response.data.msg, {
               position: 'top-right',
               autoClose: 2500,
               hideProgressBar: false,
               closeOnClick: true,
               pauseOnHover: true,
               draggable: true,
               progress: undefined,
            });

            setValues({
               storename: '',
               location: '',
            });

            handleClose(); // Close the modal first

            // Wait longer before triggering refresh to ensure server has processed the creation
            await new Promise((resolve) => setTimeout(resolve, 300));

            handleRefresh();
         }
      } catch (err) {
         console.error('Error creating store:', err);
         toast.error((err as Error)?.message || 'Server Error', {
            position: 'top-right',
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
         });
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <Form>
            <Input
               label="Store Name"
               placeholder="Enter your store name"
               name="storename"
               value={storename}
               onChange={handleChange}
               error={errors.storename}
            />
            <Input
               label="Store Location"
               placeholder="e.g San Antonio St. Davao"
               name="location"
               value={location}
               onChange={handleChange}
               error={errors.location}
            />
            <BtnWrapper>
               <div onClick={handleClose}>
                  <Btn
                     title="Cancel"
                     type="outline"
                     textcolor={colors.PRIMARY}
                     bgcolor={colors.PRIMARY}
                     size="small"
                  />
               </div>
               <div onClick={handleSubmit}>
                  <Btn
                     title="Create"
                     type={isLoading ? 'disabled' : 'normal'}
                     bgcolor={colors.PRIMARY}
                     textcolor="white"
                     size="small"
                     hasIcon={isLoading}
                     icon="loader"
                     iconPosition="right"
                  />
               </div>
            </BtnWrapper>
         </Form>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 450px;

   @media (max-width: 768px) {
      width: 100%;
   }
`;

const Form = styled.div`
   margin-top: 1rem;
   display: grid;
   flex-direction: column;
   gap: 1rem;
`;

const BtnWrapper = styled.div`
   margin-top: 1.5rem;
   display: flex;
   justify-content: flex-end;
   gap: 10px;
`;

export default CreateStore;
